import React, { useCallback, useState } from 'react';
import { Modal, Button, Alert, Row, Segmented } from 'antd';
import PropTypes from 'prop-types';
import Input from '../elements/input/Input';
import Select from '../elements/select/Select';
import { FloatingLabelSearchSelect } from '../elements/floatingLabelInput/FloatingLabelInput';
import {
  createMedic,
  fetchPromotableUsers,
  promoteUserToMedic,
} from '../../api/medics-api';
import { useInfiniteOptions, withSelectedOption } from '../../hooks/useInfiniteOptions';
import { toast } from 'react-toastify';

const CreateMedicModal = ({
  isOpen,
  onClose,
  onCreate,
  clinics = [],
  singleClinic = null,
  hideClinicSelect = false,
  createFn = createMedic,
  allowExistingUser = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // 'new' creates a brand-new account; 'existing' promotes a plain user who already
  // has one. Promotion keeps their password and upline mentor, so no password is asked
  // for and those fields are shown read-only from the selected account. The referral
  // code is prefilled from the account but stays editable.
  const [mode, setMode] = useState('new');
  const isExistingMode = allowExistingUser && mode === 'existing';

  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUserPage = useCallback(async (page, limit, search) => {
    const { data, success } = await fetchPromotableUsers({ page, limit, search: search || null });
    if (!success) return null;
    return { items: data?.users || [], totalPages: data?.pagination?.totalPages };
  }, []);
  const {
    items: loadedUsers,
    loading: usersLoading,
    onSearch: setUserSearch,
    onPopupScroll: onUsersScroll,
  } = useInfiniteOptions(fetchUserPage, {
    enabled: isOpen && isExistingMode,
    reloadKey: isOpen,
  });
  const userOptions = withSelectedOption(loadedUsers, selectedUser, '_id');

  const [modalForm, setModalForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    referralCode: '',
    clinic: null,
  });
  const [modalFormErrors, setModalFormErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
  });

  const handleResetForm = () => {
    setModalForm({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      referralCode: '',
      phone: '',
      clinic: null,
    });
    setModalFormErrors({
      firstName: false,
      lastName: false,
      email: false,
      password: false,
    });
    setSelectedUser(null);
    setUserSearch('');
  };


  const handleOnChange = (e) => {
    const { name, value } = e.target;

    setModalForm((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleOnChangeSelect = (item, label) => {
    setModalForm((prev) => {
      return {
        ...prev,
        clinic: item ? { value: item, label: label } : null,
      };
    });
  };

  const handleModeChange = (value) => {
    setMode(value);
    setError('');
    handleResetForm();
  };

  const handleSelectUser = (userId) => {
    const user = userOptions.find((u) => u._id === userId) || null;
    setSelectedUser(user);
    // Prefill with the code the account already has — it stays editable, so an admin
    // can swap it for a nicer one while promoting.
    setModalForm((prev) => ({
      ...prev,
      referralCode: user?.referralCode || '',
    }));
    setError('');
  };

  const validateInputs = () => {
    const { firstName, lastName, email, password, referralCode } = modalForm;
    if (!firstName.trim()) {
      setError('Unesite ime mentora.');
      return false;
    }
    if (!lastName.trim()) {
      setError('Unesite prezime mentora.');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Unesite validnu adresu e-pošte.');
      return false;
    }
    // password to contain at least one uppercase, one lowercase, one number and one special character
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!$%_.,#+])[A-Za-z\d!$%_.,#+]{8,}$/;
    if (!passwordRegex.test(password)) {
      setError(
        'Lozinka mora sadržati najmanje 8 karaktera, uključujući veliko i malo slovo, broj i specijalni karakter (!$%_.,#+).'
      );
      return false;
    }
    if (!referralCode.trim()) {
      setError('Unesite referral kod mentora.');
      return false;
    }
    setError('');
    return true;
  };

  const resolveClinic = () =>
    singleClinic ||
    (hideClinicSelect ? undefined : modalForm?.clinic?.value?._id);

  const handleOk = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (isExistingMode) {
        if (!selectedUser) {
          setError('Izaberite korisnika koji postaje mentor.');
          setLoading(false);
          return;
        }

        if (!modalForm.referralCode.trim()) {
          setError('Unesite referral kod mentora.');
          setLoading(false);
          return;
        }

        toast.loading('Postavljanje mentora...');

        const { success, message } = await promoteUserToMedic({
          userId: selectedUser._id,
          referralCode: modalForm.referralCode.trim(),
          clinic: resolveClinic(),
        });

        if (!success) {
          setError(message);
          toast.dismiss();
          toast.error(
            message || 'Došlo je do greške pri postavljanju mentora.'
          );
          throw new Error(message);
        }
      } else {
        const validate = validateInputs();

        if (!validate) {
          // toast.error('Some fields are invalid. Please check your form!');
          setLoading(false);
          return;
        }

        toast.loading('Kreiranje mentora...');

        const values = {
          ...modalForm,
          role: 'doctor',
          clinic: resolveClinic(),
        };

        const { success, message } = await createFn(values);

        if (!success) {
          setError(message);
          toast.dismiss();
          toast.error(message || 'Došlo je do greške pri kreiranju mentora.');
          throw new Error(message);
        }
      }

      // setDone(true);
      onCreate();
      handleResetForm();
      onClose();

      setTimeout(() => {
        // setDone(false);
        toast.dismiss();
        toast.success('Mentor uspešno kreiran!');
        setLoading(false);
        onClose();
      }, 1000);
    } catch (error) {
      if (error?.errorFields) return;
      toast.dismiss();
      toast.error(error.message || `Došlo je do greške pri kreiranju mentora.`);
      setError(error.message || 'Došlo je do greške pri kreiranju mentora.');
    } finally {
      setLoading(false);
    }
  }, [modalForm, isExistingMode, selectedUser]);

  const handleCancel = () => {
    handleResetForm();
    setError('');
    setDone(false);
    onClose();
  };

  const referredByDoctorName = selectedUser?.referredByDoctor
    ? `${selectedUser.referredByDoctor.firstName ?? ''} ${
        selectedUser.referredByDoctor.lastName ?? ''
      }`.trim()
    : '';

  return (
    <Modal
      className={'create_user_modal'}
      title="Dodaj mentora"
      open={isOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={[
        <Button
          key="create"
          type="primary"
          onClick={handleOk}
          className="btn-primary"
          disabled={loading}
          style={{ width: '150px' }}
        >
          {loading ? 'Kreiranje...' : 'Dodaj mentora'}
        </Button>,
      ]}
    >
      <form
        className={'create_user_modal_form'}
        layout="vertical"
        autoComplete="off"
      >
        {allowExistingUser && (
          <Segmented
            block
            value={mode}
            onChange={handleModeChange}
            options={[
              { label: 'Novi mentor', value: 'new' },
              { label: 'Postojeći korisnik', value: 'existing' },
            ]}
            style={{ marginBottom: 16 }}
          />
        )}

        {isExistingMode ? (
          <Row className={'create_user_modal_row'}>
            <div>
              <FloatingLabelSearchSelect
                label="Korisnik"
                placeholder="Pretraži po imenu ili e-pošti"
                value={selectedUser?._id || null}
                onChange={handleSelectUser}
                onSearch={setUserSearch}
                loading={usersLoading}
                onPopupScroll={onUsersScroll}
                options={userOptions.map((user) => ({
                  value: user._id,
                  label: `${user.firstName} ${user.lastName} (${user.email})`,
                }))}
              />
            </div>

            {selectedUser && (
              <>
                <div>
                  <Input
                    label="Ime"
                    id="existingFirstName"
                    value={selectedUser.firstName || ''}
                    readOnly
                    disabled
                  />
                </div>

                <div>
                  <Input
                    label="Prezime"
                    id="existingLastName"
                    value={selectedUser.lastName || ''}
                    readOnly
                    disabled
                  />
                </div>

                <div>
                  <Input
                    label="Adresa e-poste"
                    id="existingEmail"
                    value={selectedUser.email || ''}
                    readOnly
                    disabled
                  />
                </div>

                <div>
                  <Input
                    label="Broj telefona"
                    id="existingPhone"
                    value={selectedUser.phone || ''}
                    readOnly
                    disabled
                  />
                </div>

                {/* Prefilled with the code the account already has. Changing it is
                    allowed, but note referrals already made under the old code point
                    at this user by id, not by code, so they are unaffected. */}
                <div>
                  <Input
                    label="Referral kod"
                    id="referralCode"
                    name="referralCode"
                    placeholder="Referral kod"
                    value={modalForm.referralCode}
                    onChange={(e) => handleOnChange(e)}
                  />
                </div>

                {referredByDoctorName && (
                  <div>
                    <Input
                      label="Nadređeni mentor"
                      id="existingReferredByDoctor"
                      value={referredByDoctorName}
                      readOnly
                      disabled
                    />
                  </div>
                )}
              </>
            )}

            {!singleClinic && !hideClinicSelect && (
              <div>
                <Select
                  value={modalForm.clinic}
                  onChange={handleOnChangeSelect}
                  items={clinics}
                  placeholder="Organizacija"
                />
              </div>
            )}
          </Row>
        ) : (
          <Row className={'create_user_modal_row'}>
            <div>
              <Input
                label="Ime"
                id="firstName"
                name="firstName"
                placeholder="Ime"
                value={modalForm.firstName}
                onChange={(e) => handleOnChange(e)}
              />
            </div>

            <div>
              <Input
                label="Prezime"
                id="lastName"
                name="lastName"
                placeholder="Prezime"
                value={modalForm.lastName}
                onChange={(e) => handleOnChange(e)}
              />
            </div>

            <div>
              <Input
                type="email"
                required
                autoComplete="new-email"
                label="Adresa e-poste"
                id="email"
                name="email"
                placeholder="Adresa e-poste"
                value={modalForm.email}
                onChange={(e) => handleOnChange(e)}
              />
            </div>

            <div>
              <Input
                label="Broj telefona"
                id="phone"
                name="phone"
                placeholder="+381648883726"
                value={modalForm.phone}
                onChange={(e) => handleOnChange(e)}
              />
            </div>

            <div>
              <Input
                type="password"
                label="Lozinka"
                id="password"
                name="password"
                placeholder="Lozinka"
                value={modalForm.password}
                onChange={(e) => handleOnChange(e)}
              />
            </div>

            <div>
              <Input
                label="Referral kod"
                id="referralCode"
                name="referralCode"
                placeholder="Referral kod"
                value={modalForm.referralCode}
                onChange={(e) => handleOnChange(e)}
              />
            </div>

            {!singleClinic && !hideClinicSelect && (
              <div>
                <Select
                  value={modalForm.clinic}
                  onChange={handleOnChangeSelect}
                  items={clinics}
                  placeholder="Organizacija"
                />
              </div>
            )}
          </Row>
        )}

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
      </form>
    </Modal>
  );
};

CreateMedicModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  clinics: PropTypes.array,
  singleClinic: PropTypes.string,
  hideClinicSelect: PropTypes.bool,
  createFn: PropTypes.func,
  // Off everywhere except admin views — promoting an existing user is admin-only
  // (the API refuses the picker's user list and the promote call for anyone else).
  allowExistingUser: PropTypes.bool,
};

export default CreateMedicModal;
